import React, { useEffect, useState, useCallback } from 'react';
import { withRouter } from 'react-router-dom';
import { Link } from '@jutro/router';
import appConfig from 'app-config';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useModal } from '@jutro/components';
import { useTranslator } from '@jutro/locale';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { MetadataContent } from '@jutro/legacy/uiconfig';
import { withViewModelService } from '@xengage/gw-portals-viewmodel-react';
import { SearchService, UserService, MLAgentChoiceReportService } from 'gw-capability-gateway';
import { MLBundleService, MLStateRolloutService } from 'ml-services-common-react';
import { FormModalPopover } from 'ml-components-platform-react';
import { MLPolicyTypeUtil } from 'ml-portals-util-react';
import { MLProductCodeUtil } from 'ml-portals-util-js';
import { MLModalService } from 'ml-portals-services-react';

import mlBundleQuestionPopup from '../MLBundleQuestionPopup/MLBundleQuestionPopup';
import mlBundleQuestionPopupMsg from '../MLBundleQuestionPopup/MLBundleQuestionPopup.messages.js';

import MLQuotesAdditionalFilter from './MLQuotesAdditionalFilter';

import styles from './MLQuotesReportComponent.module.scss';
import messages from './MLQuotesReportComponent.messages';
import metadata from './MLQuotesReportComponent.metadata.json5';

import { Button } from '@jutro/legacy/components';

const MLQuotesReportComponent = (props) => {
    const modalApi = useModal();
    const {
        moreDetails,
        isDashboard,
        convertLocalDateToDate,
        formatAmountString,
        history,
        viewModelService
    } = props;

    const {
        showProgressDialog,
        showErrorModal
    } = MLModalService();

    const { MLUserTypes } = appConfig;
    const translator = useTranslator();
    const rowLimit = moreDetails ? 20 : 10;

    const { authHeader } = useAuthentication();

    const [isLoading, updateIsLoading] = useState(false);
    const [filteredJobs, updateFilteredJobs] = useState([]);
    const [resultsRowCount, updateResultsRowCount] = useState(0);
    const [filterData, updateFilterData] = useState({});
    const [isPCSorIA, updateIsPCSorIA] = useState(false);
    const [isChoiceAgentUser, updateIsChoiceAgentUser] = useState(false);
    const [isBookRollDisplay, updateIsBookRollDisplay] = useState(false);

    const { getPolicyTypeName } = MLPolicyTypeUtil();
    const { showReportModalWithQuoteID } = MLAgentChoiceReportService();

    let initialProductCodes = [];
    if (moreDetails) {
        initialProductCodes = [
            MLProductCodeUtil.getPersonalAutoCode(),
            MLProductCodeUtil.getPersonalUmbrellaCode(),
            MLProductCodeUtil.getDwellingCode(),
            MLProductCodeUtil.getRentalCode(),
            MLProductCodeUtil.getCondoCode(),
            MLProductCodeUtil.getLandlordHome(),
            MLProductCodeUtil.getLandlordCondo()
        ];
    }
    const [productCodes] = useState(initialProductCodes)

    const isBookrollQuote = (quote) => {
        return quote.bookrollIndicator === 'With Rate Capping' || quote.bookrollIndicator === 'Without Rate Capping';
    };

    const searchForAvailableQuotes = useCallback(async (request) => {
        updateIsLoading(true);

        const response = isDashboard
            ? await SearchService.searchAvailableQuoteFromCurrentUser(request, rowLimit, authHeader)
            : await SearchService.searchAvailableQuoteFromCurrentUser(request, null, authHeader);

        updateFilteredJobs(response.searchQuoteDetails);
        updateIsLoading(false);
        updateResultsRowCount(response.searchQuoteDetails.length);
    }, [authHeader, isDashboard, rowLimit]);

    useEffect(() => {
        searchForAvailableQuotes(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        UserService.getCurrentUser(authHeader).then((user) => {
            updateIsChoiceAgentUser(user.isChoiceAgent_ML);
            updateIsPCSorIA(user.userType === MLUserTypes.PCS || user.userType === MLUserTypes.IA);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setBookRollDisplay = useCallback((currentPageNumber) => {
        const startPoint = ((currentPageNumber - 1) * rowLimit);
        const endPoint = (rowLimit * currentPageNumber);

        // returns the results in multiple of {{rowLimit}} based on the page. if page 1, returns 0-19 result.
        // if page 2 returns 20-39 and so on
        const quotesResult = filteredJobs.slice(startPoint, endPoint);
        const checkBookRoll = quotesResult.filter((o) => o.bookrollIndicator && o.bookrollIndicator.search(/with/i) >= 0);
        const tempIsBookrollDisplay = checkBookRoll.length > 0;
        updateIsBookRollDisplay(tempIsBookrollDisplay);
        if (!tempIsBookrollDisplay) {
            _.set(filterData, 'bookRoll_ML', undefined);
        }
    }, [filterData, filteredJobs, rowLimit]);

    useEffect(() => {
        setBookRollDisplay(1);
    }, [setBookRollDisplay]);

    const onFilter = () => {
        searchForAvailableQuotes(filterData);
    };

    const onClearFilters = () => {
        updateFilterData({});
        updateFilteredJobs([]);
    };

    const renderQuoteCreatedCell = (quote) => {
        return convertLocalDateToDate(quote.quoteDate);
    };

    const renderCustomerName = (quote) => {
        const formatString = (str) => {
            return str
                .replace(/(\B)[^ ]*/g, (match) => (match.toLowerCase()))
                .replace(/^[^ ]/g, (match) => (match.toUpperCase()));
        };

        const getAccountName = (accountName, lastName) => {
            if (accountName.length === lastName.length) {
                return formatString(accountName);
            }

            const firstName = accountName.substring(0, accountName.length - lastName.length).trim();

            const result = `${lastName}, ${firstName}`;
            return formatString(result);
        };

        return (
            <Link to={`/accounts/${quote.accountNumber}/summary`} className={styles.linkCellValue}>
                {(getAccountName(quote.accountName, quote.lastName))}
            </Link>
        );
    };

    const renderQuoteNumberCell = (quote) => {
        const _determineRoute = (quote) => {
            if (isBookrollQuote(quote)) {
                MLBundleService.loadBundleCart(quote.accountNumber, history);
            } else {
                MLBundleService.updateBundleAndGotoAgentBundleCart(quote.accountNumber, quote.quoteNumber, showProgressDialog, viewModelService, showErrorModal, history, authHeader);
            }
        }

        const redirectQuote = () => {
            if (quote.bundled) {
                MLBundleService.checkIsCRBridgedQuote(quote.quoteNumber, authHeader)
                    .then((isCRBridgeQuote) => {
                        if(isCRBridgeQuote && isCRBridgeQuote.length > 0) {
                            // Check state rollout eligible
                            return MLStateRolloutService.checkIsCRWizardEligible(quote.quoteNumber, authHeader)
                                .then((isCREligible) => {
                                    if(isCREligible) {
                                        const quoteEntryInfo = {
                                            account: quote.accountNumber,
                                            quoteID: quote.quoteNumber,
                                            postalCode: quote.postalCode
                                        };
                                        history.push('/quoteentry', quoteEntryInfo);
                                    } else {
                                        _determineRoute(quote);
                                    }
                                })
                        } else {
                            _determineRoute(quote)
                        }
                    });
            } else if (isBookrollQuote(quote) || quote.isAlaska7DayQuote) {
                if (quote.premium) {
                    // $state.go('quoteentry', {quoteID: quote.quoteNumber, postalCode: quote.postalCode});
                } else {
                    const modalTitle = translator(messages.quoteNotComplete);
                    let modalMsg = '';
                    if (isBookrollQuote(quote)) {
                        modalMsg = translator(messages.contactBookManager);
                    } else if (isPCSorIA) {
                        modalMsg = translator(messages.quoteNotCompleteCall);
                    } else {
                        modalMsg = translator(messages.quoteToCompleteInPC);
                    }
                    showErrorModal(modalTitle, modalMsg);
                }
            } else {
                const componentProps = {
                    title: mlBundleQuestionPopupMsg.savedQuote,
                    overrideProps: {
                        accountNumber: quote.accountNumber,
                        quoteNumber: quote.quoteNumber,
                        postalCode: quote.postalCode,
                        history,
                        viewModelService,
                        authHeader
                    },
                    CustomComponent: mlBundleQuestionPopup,
                    showFooterOnCustomComponent: false,
                    additionalPEStyle: 'customFooterModal'
                };

                modalApi.showModal(
                    <FormModalPopover {...componentProps} />
                );
            }
        };

        return (
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            <Link onClick={redirectQuote} className={styles.linkCellValue}>
                {quote.quoteNumber}
            </Link>
        )
    };

    const renderBundleCell = (quote) => {
        return quote.bundled ? 'Yes' : 'No';
    };

    const renderPolicyTypeCell = (quote) => {
        return getPolicyTypeName(quote.policyType);
    };

    const renderInitiatorCell = (quote) => {
        if (isChoiceAgentUser && quote.isChoiceAgentSubmission_ML) {
            const openAgentChoiceModal = () => {
                showReportModalWithQuoteID(quote.quoteNumber);
            };

            return (
                <Button className={styles.initiatorButton} onClick={openAgentChoiceModal} type="text">{translator(messages.choiceAgent)}</Button>
            )
        }
        return quote.initiator;
    };

    const renderPremiumCell = (quote) => {
        if (!quote.premium) {
            return '$-';
        }
        return formatAmountString(quote.premium);
    };

    const renderBookrollCell = (quote) => {
        return quote.bookrollIndicator
    };

    const onViewAllButtonClicked = () => {
        const reportView = 'quotes-report';
        history.push('/activity-reports', {reportView : reportView} );
    }

    const onConfigChange = (configs) => {
        setBookRollDisplay(configs.page + 1);
    }

    const overrideProps = {
        statusDesc: {
            visible: isLoading,
            content: (<span tabindex="0" role="alert" aria-live="polite">{translator(messages.loading)}</span>)
        },
        quotesResultsFilterComponent: {
            visible: !isDashboard,
            filterData,
            onFilter,
            onClearFilters,
            resultsRowCount,
            updateFilterData,
            productCodes: moreDetails ? productCodes : undefined,
            additionalFiltersComponent: MLQuotesAdditionalFilter,
            additionalFilterProps: {
                isBookRollDisplay,
                isPCSorIA
            }
        },
        quotesReportTable: {
            data: _.orderBy(filteredJobs, 'quoteDate', 'asc'),
            visible: !isLoading,
            defaultConfig: {
                pageSize: rowLimit
            },
            noDataText: (<span tabindex="0" role="alert" aria-live="polite">{translator(messages.noQuotesToShow)}</span>),
            showPagination: moreDetails,
            onConfigChange,
            tableLabel: translator(messages.recentQuotes)
        },
        viewAllQuotesButton: {
            visible: isDashboard,
            onClick: onViewAllButtonClicked,
            mlTagManagerLinkClick: "Dashboard_activityReport_viewAll_quotes"
        },
        bookrollColumn: {
            visible: (isPCSorIA) && isBookRollDisplay
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            renderQuoteCreatedCell,
            renderCustomerName,
            renderQuoteNumberCell,
            renderBundleCell,
            renderPolicyTypeCell,
            renderBookrollCell,
            renderInitiatorCell,
            renderPremiumCell
        },
        resolveClassNameMap: styles
    };

    return (
        <MetadataContent
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            {...resolvers} />
    );
};

MLQuotesReportComponent.propTypes = {
    moreDetails: PropTypes.bool.isRequired,
    isDashboard: PropTypes.bool.isRequired,
    history: PropTypes.shape({push: PropTypes.func}).isRequired,
    convertLocalDateToDate: PropTypes.func.isRequired,
    formatAmountString: PropTypes.func.isRequired
}

export default withRouter(withViewModelService(MLQuotesReportComponent));
